import PageErrorFallback from '@app/components/layout/page/components/page-error-fallback';
import { ErrorBoundary } from '@sentry/react';
import { useGetSoldFoodAndDrinkItem } from '@shared/api/hooks/sold-goods/food-and-drink';
import {
  FoodActivityRetrieve,
  FoodOperationActivityDetail,
} from '@shared/api/types';
import { useHistory } from '@shared/providers/history-provider';
import { QueryObserverResult } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

export type TContext = {
  item: FoodOperationActivityDetail;
  status: 'error' | 'success' | 'loading';
  foodActivityUuid?: string;
  refetchItem: () => Promise<
    QueryObserverResult<FoodActivityRetrieve, unknown>
  >;
  referrer?: string;
};

const Context = createContext<TContext | null>(null);

export function useSoldFoodAndDrinkItem() {
  return useContext(Context) as TContext;
}

export default function SoldFoodAndDrinkItemContext() {
  const params = useParams();
  const foodActivityUuid = params.item_uuid;
  const history = useHistory();
  const [referrer] = useState<string | undefined>(history.previousLocation);

  const { data, status, refetch } = useGetSoldFoodAndDrinkItem({
    activityUuid: foodActivityUuid!,
  });

  const refetchItem = useCallback(() => {
    return refetch();
  }, [refetch]);

  const context: TContext = {
    item: data!,
    status,
    foodActivityUuid,
    refetchItem,
    referrer,
  };

  return (
    <>
      <ErrorBoundary fallback={<PageErrorFallback />}>
        <Context.Provider value={context}>
          <Outlet />
        </Context.Provider>
      </ErrorBoundary>
    </>
  );
}
